/**
 * @file コンポーネント：フル動画ページスレッドポップアップ詳細ページ ロジック管理
 */

// ライブラリ読み込み
import React, { useEffect, useRef, useState } from "react";

// Presenter読み込み
import { Presenter } from "./presenter";

// 関数読み込み
import {
    fnReplaceLocalizedText,
    fnHandleApiErrors,
    fnHandleApiSuccess,
    fnZeroPadding,
} from "~/src/functions/common";

// コンポーネントを返す
export const PopupThreadDetail = (props) => {
    // ローカライズデータ
    const localized = window.listLocalized;

    /**
     * 変数
     */
    const numDisplay = 10; // 返信コメントの表示数

    /**
     * 状態管理
     */
    const [deleteReplyId, setDeleteReplyId] = useState(null); // 削除対象の返信コメントID
    const [errorMessageDetail, setErrorMessageDetail] = useState(null); // 詳細ページに表示するエラーメッセージ
    const [isListReplyLoaded, setIsListReplyLoaded] = useState(false); // 返信コメント一覧の取得が完了したかどうか
    const [isOpenModalThreadConfirm, setIsOpenModalThreadConfirm] = // スレッド確認モーダルの表示
        useState(false);
    const [listReply, setListReply] = useState(null); // 返信コメント一覧データ
    const [replyPaginationCurrentPage, setReplyPaginationCurrentPage] = // 返信コメント一覧ページネーションの現在位置
        useState(1);
    const [replyPaginationlist, setReplyPaginationlist] = useState([]); // 返信コメント一覧ページネーションリスト
    const [replySortType, setReplySortType] = useState("createDesc"); // 返信コメント一覧ソートタイプ
    const [replyCount, setReplyCount] = useState(
        props.selectedThread.replyCount
    ); // 返信コメント数
    const [selectedReply, setSelectedReply] = useState(null); // 選択中の返信コメント

    /**
     * 要素参照
     */
    const refContainer = useRef(null);
    const refTextareaReply = useRef(null);

    /**
     * ポップアップ見出し設定
     */
    useEffect(() => {
        props.setPopupHeadline(
            `<span>${props.selectedThread.title}</span><span>(${replyCount})</span>`
        );
    }, [props.selectedThread, replyCount]);

    /**
     * 返信コメントの再取得
     */
    useEffect(() => {
        fnGetReplyList();
    }, [replyCount, replyPaginationCurrentPage, replySortType]);

    /**
     * 返信コメントページネーション設定
     */
    useEffect(() => {
        const tempListPagination = [];
        // ページ数設定
        const pageCount = Math.ceil(replyCount / numDisplay);

        for (let i = 0; i < pageCount; i++) {
            const item = {
                href: "#",
                isCurrent: i + 1 === replyPaginationCurrentPage ? true : false,
                onClick: (e) => {
                    e.preventDefault();
                    setReplyPaginationCurrentPage(i + 1);
                    fnClearMessage();
                },
            };
            tempListPagination.push(item);
        }
        setReplyPaginationlist(tempListPagination);
    }, [replyCount, replyPaginationCurrentPage]);

    /**
     * ソートタイプが更新時、カレントページを1にする
     */
    useEffect(() => {
        setReplyPaginationCurrentPage(1);
    }, [replySortType]);

    /**
     * 返信コメント編集タイムライン追加処理
     */
    const fnAddTimelineEditReply = () => {
        const target = refTextareaReply.current;
        // 動画時間を取得
        const elCurrentTime = document.querySelector(
            "#full .rmp-ext-progress__current"
        );
        let tempCurrentTime = "";
        for (const element of Array.from(elCurrentTime.childNodes)) {
            if (element.nodeName === "#text") {
                tempCurrentTime += element.nodeValue.trim();
            }
        }
        const arrayCurrentTime = tempCurrentTime.split(":").map((t) => {
            return fnZeroPadding(t, 2); // 0埋め2桁に直す
        });
        // hh:mm:ssに不足している場合、不足分だけ00を先頭に追加
        for (let i = 0; i < 3 - arrayCurrentTime.length; i++) {
            arrayCurrentTime.unshift("00");
        }
        const currentTime = arrayCurrentTime.join(":");
        target.value =
            target.value.substr(0, target.selectionStart) +
            " [" +
            currentTime +
            "] " +
            target.value.substr(target.selectionStart);
    };

    /**
     * AddTimeline部分をリンク化して表示
     */
    const fnAddTimelineLink = (replyContext) => {
        if (replyContext) {
            const tempContextText = replyContext.split(/\[\d\d:\d\d:\d\d\]/);
            const tempContextTimeline =
                replyContext.match(/\[\d\d:\d\d:\d\d\]/g);
            const arrayContext = [];
            if (tempContextTimeline !== null) {
                tempContextTimeline.forEach((timeline, index) => {
                    arrayContext.push(fnEscapeHtml(tempContextText[index]));
                    const timelineLink = timeline.replace(
                        /(\[)(\d\d:\d\d:\d\d)(\])/,
                        '<a data-time="$2" href="#">$&</a>'
                    );
                    arrayContext.push(timelineLink);
                });
                arrayContext.push(
                    fnEscapeHtml(tempContextText[tempContextText.length - 1])
                );
                return arrayContext.join("");
            } else {
                return fnEscapeHtml(replyContext);
            }
        }
    };

    /**
     * 返信コメント削除をキャンセル時の処理
     */
    const fnCancelDeleteReply = () => {
        setDeleteReplyId(null);
    };

    /**
     * 返信コメントソートタイプの変更
     */
    const fnChangeSortType = (e) => {
        setReplyPaginationCurrentPage(1);
        setReplySortType(e.target.options[e.target.selectedIndex].value);
    };

    /**
     * メッセージをクリア
     */
    const fnClearMessage = () => {
        props.setMessageDetail(null);
        setErrorMessageDetail(null);
    };

    /**
     * htmlエスケープ処理
     */
    const fnEscapeHtml = (str) => {
        return str
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    };

    /**
     * 返信コメント編集ボタンを押した処理
     */
    const fnOnClickReplyEditButton = (reply, isEditReply) => {
        if (!selectedReply || (selectedReply && !isEditReply)) {
            // 編集
            setSelectedReply(reply);
            const tempReplyContext = fnRemoveTimelineLink(reply.context);
            refTextareaReply.current.value = fnUnEscapeHTML(tempReplyContext);
            refTextareaReply.current.focus();
        } else {
            // キャンセル
            setSelectedReply(null);
            refTextareaReply.current.value = "";
        }
    };

    /**
     * AddTimeline部分のリンクを削除
     */
    const fnRemoveTimelineLink = (replyContext) => {
        if (replyContext) {
            const tempReplyContext = replyContext.replace(
                /<a data-time="\d\d:\d\d:\d\d" href="#">|<\/a>/g,
                ""
            );
            return tempReplyContext;
        }
    };

    /**
     * ポップアップコンテナのスクロールトップ処理
     */
    const fnScrollToTop = () => {
        refContainer?.current?.scrollTo({
            behavior: "smooth",
            top: 0,
        });
    };

    /**
     * エラーメッセージを表示する際の処理
     */
    const fnSetErrorMessageDetail = (message) => {
        props.setMessageDetail(null); // 通常メッセージをfalsyな値にしないと通常メッセージが優先されて表示される
        setErrorMessageDetail(message);
        fnScrollToTop();
    };

    /**
     * htmlアンエスケープ処理
     */
    const fnUnEscapeHTML = (str) => {
        return str
            .replace(/(&lt;)/g, "<")
            .replace(/(&gt;)/g, ">")
            .replace(/(&quot;)/g, '"')
            .replace(/(&#39;)/g, "'")
            .replace(/(&amp;)/g, "&");
    };

    /**
     * スレッド削除
     */
    const fnDeleteThread = () => {
        const api = "/api/threads/" + props.selectedThread.threadId;
        const token = document
            .getElementById("requestverificationtoken")
            .getAttribute("value");
        const options = {
            headers: {
                "Content-Type": "application/json",
                RequestVerificationToken: token,
            },
            method: "DELETE",
        };
        fetch(api, options)
            .then(fnHandleApiErrors)
            .then((response) => {
                if (response.status === 200) {
                    props.setMessageList(
                        localized["Message.SuccessDeleteThread"]
                    );
                    props.setCurrentPage("list");
                }
            })
            .catch((error) => {
                console.error(error);
                fnSetErrorMessageDetail(localized["Message.FailDeleteThread"]);
            });
    };

    /**
     * 返信コメント一覧データ取得
     */
    const fnGetReplyList = (
        operationId = props.operationId,
        threadId = props.selectedThread.threadId,
        sortType = replySortType,
        limit = numDisplay,
        offset = numDisplay * replyPaginationCurrentPage - numDisplay
    ) => {
        const api =
            "/api/thread/replies/" +
            operationId +
            "/" +
            threadId +
            "/" +
            sortType +
            "/" +
            limit +
            "/" +
            offset;
        fetch(api)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                // 200 OK
                if (result !== null) {
                    const tempListReply = result.map((reply) => {
                        const tempReply = {
                            context: fnAddTimelineLink(reply.context),
                            createdAt: reply.createdAt,
                            dateTimeAgo: props.fnGetDatetimeAgo(
                                reply.createdAt
                            ),
                            isOwner: reply.isOwner,
                            isRating: reply.isRating,
                            isSurgeon: reply.isSurgeon,
                            isThreadOwner: reply.isThreadOwner,
                            ratingCount: reply.ratingCount,
                            threadId: reply.threadId,
                            threadReplyId: reply.threadReplyId,
                            updatedAt: reply.updatedAt,
                            userName: reply.userName,
                        };
                        return tempReply;
                    });
                    setListReply(tempListReply);
                }
                // 204 No Content
                if (result === null) {
                    setListReply(null);
                }
                setIsListReplyLoaded(true);
            })
            .catch((error) => {
                console.error(error);
                fnSetErrorMessageDetail(localized["Message.FailGetReplyList"]);
                setIsListReplyLoaded(true);
            });
    };

    /**
     * 返信コメント新規作成、編集
     */
    const fnNewEditReply = (method) => {
        const context = refTextareaReply.current.value;

        // 返信コメントの未入力チェック
        if (!context) {
            fnSetErrorMessageDetail(localized["Message.ErrorIncomplete"]);
            return;
        }

        // 返信コメントが500文字以内かチェック
        if (context.length > 500) {
            fnSetErrorMessageDetail(
                fnReplaceLocalizedText(
                    localized["ErrorMessage.CharacterOver"],
                    [localized["Label.ThreadBody"], "500"]
                )
            );
            return;
        }

        // 新規作成：スレッドID or 編集：スレッドリプライID
        const id = !selectedReply
            ? props.selectedThread.threadId
            : selectedReply.threadReplyId;
        const api = "/api/thread/replies/" + id;
        const token = document
            .getElementById("requestverificationtoken")
            .getAttribute("value");
        const options = {
            body: JSON.stringify({
                context,
            }),
            headers: {
                "Content-Type": "application/json",
                RequestVerificationToken: token,
            },
            method,
        };
        fetch(api, options)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    props.setMessageDetail(
                        localized[
                            !selectedReply
                                ? "Message.SuccessCommentReply"
                                : "Message.SuccessCommentEdit"
                        ]
                    );
                    refTextareaReply.current.value = "";
                    setReplyPaginationCurrentPage(1);
                    if (!selectedReply) {
                        // 投稿時の処理
                        setReplyCount(replyCount + 1);
                    } else {
                        // 更新時の処理
                        setSelectedReply(null);
                        fnGetReplyList();
                    }
                    fnScrollToTop();
                }
            })
            .catch((error) => {
                console.error(error);
                fnSetErrorMessageDetail(
                    localized[
                        !selectedReply
                            ? "Message.FailCommentReply"
                            : "Message.FailCommentEdit"
                    ]
                );
            });
    };

    /**
     * 返信コメント削除
     */
    const fnDeleteReply = () => {
        const api = "/api/thread/replies/" + deleteReplyId;
        const token = document
            .getElementById("requestverificationtoken")
            .getAttribute("value");
        const options = {
            headers: {
                "Content-Type": "application/json",
                RequestVerificationToken: token,
            },
            method: "DELETE",
        };
        fetch(api, options)
            .then(fnHandleApiErrors)
            .then((response) => {
                if (response.status === 200) {
                    fnGetReplyList();
                    setDeleteReplyId(null);
                    setReplyCount(replyCount - 1);
                    props.setMessageDetail(
                        localized["Message.SuccessCommentDelete"]
                    );
                    setIsOpenModalThreadConfirm(false);
                    fnScrollToTop();
                }
            })
            .catch((error) => {
                fnSetErrorMessageDetail(localized["Message.FailCommentDelete"]);
                setIsOpenModalThreadConfirm(false);
                console.error(error);
            });
    };

    /**
     * 返信コメントの評価ボタン処理
     */
    const fnRateReply = (threadReplyId) => {
        const api = "/api/thread/replies/" + threadReplyId + "/rating";
        const token = document
            .getElementById("requestverificationtoken")
            .getAttribute("value");
        const options = {
            headers: {
                "Content-Type": "application/json",
                RequestVerificationToken: token,
            },
            method: "POST",
        };
        fetch(api, options)
            .then(fnHandleApiErrors)
            .then((response) => {
                if (response.status === 200) {
                    fnGetReplyList();
                }
            })
            .catch((error) => {
                console.error(error);
                fnSetErrorMessageDetail(localized["Message.FailCommentRate"]);
            });
    };

    // Presenterに渡す
    return (
        <Presenter
            {...props}
            deleteReplyId={deleteReplyId}
            errorMessageDetail={errorMessageDetail}
            fnAddTimelineEditReply={fnAddTimelineEditReply}
            fnCancelDeleteReply={fnCancelDeleteReply}
            fnChangeSortType={fnChangeSortType}
            fnClearMessage={fnClearMessage}
            fnDeleteReply={fnDeleteReply}
            fnDeleteThread={fnDeleteThread}
            fnEscapeHtml={fnEscapeHtml}
            fnNewEditReply={fnNewEditReply}
            fnOnClickReplyEditButton={fnOnClickReplyEditButton}
            fnRateReply={fnRateReply}
            isListReplyLoaded={isListReplyLoaded}
            isOpenModalThreadConfirm={isOpenModalThreadConfirm}
            listReply={listReply}
            refContainer={refContainer}
            refTextareaReply={refTextareaReply}
            replyCount={replyCount}
            replyPaginationlist={replyPaginationlist}
            setDeleteReplyId={setDeleteReplyId}
            selectedReply={selectedReply}
            setIsOpenModalThreadConfirm={setIsOpenModalThreadConfirm}
            setSelectedReply={setSelectedReply}
        />
    );
};
